// #region angular Imports
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  inject,
  OnInit,
  Renderer2,
  signal,
  Type,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
// #endregion

// #region app imports
import { BusinessAreaEnum } from 'app/core/enums/generated/BusinessAreaEnum';
import { FeatureFlagEnum } from 'app/core/enums/generated/FeatureFlagEnum';
import { NotificationType } from 'app/core/enums/generated/NotificationType';
import { PermissionRole } from 'app/core/enums/generated/PermissionRole';
import { SecurityService } from 'app/core/security/security.service';
import { BounceAlertComponent } from 'app/shared/bounce-alert/bounce-alert.component';
import { SystemNotificationVm } from 'app/shared/generated/Administration/Models/SystemNotificationVm';
import { FeatureFlagVm } from 'app/shared/generated/PCG/Models/FeatureFlagVm';
import { QuickUserInfoComponent } from 'app/shared/quick-user-info/quick-user-info.component';
import { QuickUserInfoService } from 'app/shared/quick-user-info/quick-user-info.service';
import { GlobalService } from 'app/shared/services/global.service';
import { SharedModule } from 'app/shared/shared.module';
import { NotificationService } from '../../../notification.service';
import { PushNotificationComponent } from '../../push-notification/push-notification.component';
import { CustomWidgetDashboard } from './custom-widget-dashboard/custom-widget-dashboard.component';
import { DashboardNotificationsWidgetComponent } from './widgets/notifications-widget/notifications-widget.component';
import { RecallsWidgetComponent } from './widgets/recalls-widget/recalls-widget.component';
import { DashboardRefillWidgetComponent } from './widgets/refill-widget/refill-widget.component';
import { SysInvShortcutsComponent } from './widgets/sys-inv-shortcuts/sys-inv-shortcuts.component';
// #endregion

@Component({
    selector: 'pcg-dashboard'
    , templateUrl: './dashboard.component.html'
    , styleUrl: './dashboard.component.scss'
    , imports: [
        SharedModule
        , PushNotificationComponent
        , QuickUserInfoComponent
        , CustomWidgetDashboard
        , BounceAlertComponent
        , SysInvShortcutsComponent
    ]
    , changeDetection: ChangeDetectionStrategy.Eager
    , host: { '(window:resize)': 'onResize()' }
})
export class DashboardComponent implements OnInit, AfterViewInit {

    // #region injections
    renderer = inject(Renderer2);
    sec = inject(SecurityService);
    httpClient = inject(HttpClient);
    title = inject(Title);
    infoService = inject(QuickUserInfoService);
    router = inject(Router);
    ns = inject(NotificationService);
    // #endregion

    // #region ViewContainerRef to dynamically load widgets into
    @ViewChild('widgetsContainer', { read: ViewContainerRef, static: true }) widgetsContainer!: ViewContainerRef;
    // #endregion

    // #region models
    sysNotifsList: SystemNotificationVm[] = [];
    widgets: any = [];
    // #endregion

    // #region special variables
    /*
        Classes set to generated widgets, if you want to a use a non-global css class
        go to component css file and use :host-context(.class-name) as the selector
    */
    defaultClasses = ['col-12', 'col-lg-6', 'col-xl-4', 'mb-3', 'px-4', 'px-lg-3'];
    MAX_PER_ROW = 3;

    // #endregion

    // #region numbers
    featureFlagEnum = FeatureFlagEnum.NewDashboard;
    showCustomDashboard = false;
    // #endregion

    // #region booleans
    showWindow = false;
    isMobile = signal<boolean>(false);
    doneLoading = false;
    // #endregion

    // #region HostListeners
    onResize() { this.isMobile.set(GlobalService.setIsMobile(window.innerWidth)); }
    // #endregion

    ngOnInit() {
        this.title.setTitle('Dashboard');
        this.isMobile.set(GlobalService.setIsMobile(window.innerWidth));
        this.checkForSystemNotification();
        this.infoService.user.set(this.sec.getUser());
        // First successful login of the day for an Issuance User / User Admin: have them confirm their facility
        const dailyPromptMode = this.infoService.consumeDailyFacilityPrompt(this.infoService.user()?.id!);
        if (dailyPromptMode) { this.infoService.promptFacilityConfirmation(dailyPromptMode); }
        const foundFlag: FeatureFlagVm | undefined = this.infoService
            .user()
            ?.featureFlags?.find((o) => o.featureFlagType == this.featureFlagEnum);
        if (foundFlag) { this.showCustomDashboard = true; } 
        else {
            this.setWidgets();
            this.loadComponents();
        }
    }

    ngAfterViewInit(): void { this.doneLoading = true; }

    setWidgets() {
        this.widgets = [
            {
                component: DashboardNotificationsWidgetComponent
                , classes: this.defaultClasses
                , notificationType: NotificationType.Notification                
            }
            , {
                component: DashboardNotificationsWidgetComponent
                , classes: this.defaultClasses
                , notificationType: NotificationType.Chat
            }
        ];

        const hasNdcAccess = this.sec.hasModuleAccess(
            [ BusinessAreaEnum.NDC ]
            , SecurityService.setMinRole(PermissionRole.User)
        );

        const canRefill = this.sec.hasModuleAccess(
            [ BusinessAreaEnum.Inventory ]
            , [ PermissionRole.User ]
            , false
        );

        if (canRefill) { this.widgets.push({ component: DashboardRefillWidgetComponent, classes: this.defaultClasses }); }
        if (hasNdcAccess) { this.widgets.push({ component: RecallsWidgetComponent, classes: this.defaultClasses }); }
    }

    // Sets col size depending on max per row, making first widget take up any extra space
    //**CURRENTLY not in use, had feedback where widget was 'too big', keeping code just not calling it **//
    getClasses() {
        const totalWidgets = this.widgets.length;
        const widgetSize = Math.floor(12 / this.MAX_PER_ROW);
        const remainder = totalWidgets % this.MAX_PER_ROW;
        const missingSpots = remainder == 0 ? 0 : this.MAX_PER_ROW - remainder;
        const firstSize = widgetSize + widgetSize * missingSpots;

        this.widgets.forEach((widget: { classes: string[] }, index: number) => {
            widget.classes.push(...this.defaultClasses);

            if (totalWidgets === 1) { widget.classes.push('col-12'); } 
            else if (
                remainder > 0 
                && index === 0
            ) {
                widget.classes.push(`col-xl-${firstSize}`);
                if (
                    missingSpots % 2 == 0 
                    || totalWidgets == 2
                ) { widget.classes.push('col-lg-6'); }
            } else { widget.classes.push(`col-xl-${widgetSize}`, `col-lg-6`); }
        });
    }

    loadComponents() {
        this.widgets.forEach(
            (component: { component: Type<unknown>; classes: any[]; notificationType: NotificationType }) => {
                const ref = this.widgetsContainer.createComponent(component.component);
                component.classes.forEach((className) => {
                    this.renderer.addClass(ref.location.nativeElement, className);
                });
                // Set notificationType input if present
                if (component.notificationType !== undefined) {
                    ref.setInput('notificationType', component.notificationType);
                }
            },
        );
    }

    checkForSystemNotification() {
        this.httpClient
            .get<SystemNotificationVm[]>(`api/Administration/SystemNotification/CheckForSystemNotification?ngsw-bypass=true`)
            .subscribe((lst: SystemNotificationVm[]) => {
                const dismissed: number[] | null = JSON.parse(localStorage.getItem('systemNotification') ?? 'null');
                const ids = lst?.map(n => n?.id) ?? [];
                const idsNotDismissed = ids.filter(id => !dismissed?.includes(id));

                if (idsNotDismissed.length > 0) {
                    this.sysNotifsList = lst?.filter(o => idsNotDismissed.includes(o?.id));
                }

                this.showWindow = this.sysNotifsList.some(
                    n => n.startDate != null && n.endDate != null
                );
            });
    }

    dismiss(vm: SystemNotificationVm) {
        const dismissed: number[] = JSON.parse(localStorage.getItem('systemNotification') ?? '[]');
        dismissed.push(vm.id);
        localStorage.setItem('systemNotification', JSON.stringify(dismissed));

        const remainingIds = this.sysNotifsList.map(n => n?.id).filter(id => !dismissed.includes(id));
        this.sysNotifsList = remainingIds.length > 0
            ? this.sysNotifsList.filter(o => remainingIds.includes(o?.id))
            : [];
    }
}
